import { usePrevious } from '@dnd-kit/utilities';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { uniqBy } from 'lodash-es';
import React, { useEffect } from 'react';

import useContractCampaigns from '../../hooks/useContractCampaigns';
import { useContractManagerContext } from '../../hooks/useContractManagerContext';
import useContractTargetedCreatives from '../../hooks/useContractTargetedCreatives';
import oneDspTheme from '../../theme/oneDspTheme';
import CreativesDataGrid from '../Creatives/CreativesDataGrid';

function ContractTargetedCreatives({ setCount }: { setCount: (count: number) => void }) {
  const {
    contract,
    selectedAdGroups,
    setSelectedAdGroupIds,
    selectedLineItems,
    selectedLineItemIds,
    setSelectedLineItemIds,
    selectedAdGroupIds,
  } = useContractManagerContext();

  const { data: campaignRawData, isLoading: isCampaignLoading, refetch } = useContractCampaigns(contract.contractId);
  const { data: creativeData, isLoading } = useContractTargetedCreatives(contract.contractId);

  const allCampaignIds =
    campaignRawData?.collection
      ?.filter((campaign: any) =>
        selectedLineItemIds.length > 0 ? selectedLineItemIds.includes(campaign.insertionOrderId) : true
      )
      .map((campaign: any) => campaign.id) || [];

  const rowsWithDuplicateIds = creativeData?.collection?.filter((row: any) => {
    if (selectedAdGroupIds.length === 0) {
      return allCampaignIds.includes(row?.association?.campaignId);
    }
    return selectedAdGroupIds.includes(row.association.campaignId);
  });

  const rows = uniqBy(rowsWithDuplicateIds, 'id');

  const previousRowCount = usePrevious(rows?.length);

  useEffect(() => {
    if (rows?.length !== previousRowCount) {
      setCount(rows?.length);
    }
  }, [rows?.length, setCount, previousRowCount]);

  return (
    <Stack spacing={2}>
      {(selectedLineItems.length > 0 || selectedAdGroups.length > 0) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Stack justifyContent="center" alignItems="center">
            <Typography color={oneDspTheme.palette.text.secondary}>Showing Creatives for:</Typography>
          </Stack>
          {selectedLineItems.map((lineItem: any) => (
            <Stack py={0.5}>
              <Chip
                key={lineItem.id}
                label={lineItem.name}
                variant="outlined"
                color="primary"
                onDelete={() => {
                  // Remove the line item from the selectedLineItemIds
                  const newSelectedLineItemIds = selectedLineItemIds.filter((id) => id !== lineItem.id);
                  setSelectedLineItemIds(newSelectedLineItemIds);
                }}
              />
            </Stack>
          ))}
          {selectedAdGroups.map((adGroup: any) => (
            <Stack py={0.5}>
              <Chip
                key={adGroup.id}
                label={adGroup.name}
                variant="outlined"
                color="secondary"
                onDelete={() => {
                  // Remove the ad group from the selectedAdGroupIds
                  const newSelectedAdGroupIds = selectedAdGroupIds.filter((id) => id !== adGroup.id);
                  setSelectedAdGroupIds(newSelectedAdGroupIds);
                }}
              />
            </Stack>
          ))}
          <Button
            variant="text"
            onClick={() => {
              setSelectedLineItemIds([]);
              setSelectedAdGroupIds([]);
            }}
          >
            Clear Filters
          </Button>
        </Stack>
      )}
      <CreativesDataGrid
        creativesList={{ collection: rows || [] }}
        isLoading={isLoading || isCampaignLoading}
        refetch={refetch}
        contractId={contract.contractId}
      />
    </Stack>
  );
}

export default ContractTargetedCreatives;
